import { t } from "../i18n/runtime";
import type { NationId } from "@cwb/core";
import { nationColorCss, useGameStore } from "../stores/gameStore";
import { CREDIT, formatEndReason, formatEndTitle } from "../i18n";
import { Bar, Button, formatMoney } from "./primitives";

/** End-of-session breakdown (§6.2). */

export function ResultScreen() {
  const result = useGameStore((s) => s.result);
  const mapNations = useGameStore((s) => s.map?.nations);

  if (!result) return null;
  const { score } = result;

  const nameOf = (id: NationId): string =>
    mapNations?.find((n) => n.id === id)?.name ?? id.toUpperCase();

  const shares = (
    Object.entries(result.territoryShare) as Array<[NationId, number]>
  ).sort((a, b) => b[1] - a[1]);

  return (
    <div className="modal-scrim">
      <div className="result">
        <div className="result__title">{formatEndTitle(result.reason)}</div>
        <div className="result__reason">{formatEndReason(result.reason)}</div>

        <div className="result__body">
          <section
            className="result__summary-pane"
            aria-label={t("ResultScreen.game_result_summary")}
          >
            <div className="label result__summary-title">
              {t("ResultScreen.result_summary")}
            </div>
            <dl className="result__score">
              <dt>{t("ResultScreen.cash")}</dt>
              <dd>{formatMoney(score.cash)}</dd>
              <dt
                title={t(
                  "ResultScreen.unsold_inventory_is_valued_at_its_purchase",
                )}
              >
                {t("ResultScreen.inventory_cost")}
              </dt>
              <dd>{formatMoney(score.inventoryValue)}</dd>
              <dt
                title={t("ResultScreen.face_value_of_debt_owed_by_surviving")}
              >
                {CREDIT.receivables}{" "}
                <span className="result__aside">
                  {t("ResultScreen.face_value")}
                </span>
              </dt>
              <dd>{formatMoney(score.receivables)}</dd>
              <dt
                title={t(
                  "ResultScreen.purchase_cost_of_goods_delivered_on_credit",
                )}
              >
                {CREDIT.receivables}{" "}
                <span className="result__aside">{t("ResultScreen.cost")}</span>
              </dt>
              <dd>{formatMoney(score.receivableCost)}</dd>
              <dt title={t("ResultScreen.unpaid_debt_of_eliminated_nations")}>
                {CREDIT.badDebt}
              </dt>
              <dd className={score.badDebt > 0 ? "tone-error" : undefined}>
                {formatMoney(score.badDebt)}
              </dd>
              <dt>{t("ResultScreen.trading_profit_loss")}</dt>
              <dd
                className={
                  score.tradingProfit < 0 ? "tone-error" : "tone-success"
                }
              >
                {formatMoney(score.tradingProfit)}
              </dd>
              <dt>{t("ResultScreen.net_worth")}</dt>
              <dd>{formatMoney(score.netWorth)}</dd>
              <div className="result__final" style={{ display: "contents" }}>
                <dt>{t("ResultScreen.final_score")}</dt>
                <dd>{formatMoney(score.finalScore)}</dd>
              </div>
            </dl>

            {/*
             * The write-off already happened: the balance left 미수금 the moment the
             * client stopped existing. Saying so stops the 대손 row reading like a
             * penalty that has yet to be taken off the total.
             */}
            {score.badDebt > 0 ? (
              <div className="result__bad-debt-note muted">
                {t(
                  "ResultScreen.bad_debt_was_already_removed_from_receivables",
                )}
              </div>
            ) : null}

            <div className="label result__territory-title">
              {t("ResultScreen.territory_share")}
            </div>
            <div className="result__territory">
              {shares.map(([nationId, share]) => (
                <div className="result__terr-row" key={nationId}>
                  <span
                    style={{
                      color: nationColorCss(
                        mapNations?.find((n) => n.id === nationId)?.color,
                      ),
                    }}
                  >
                    {nameOf(nationId)}
                  </span>
                  <Bar
                    value={share}
                    max={1}
                    tone={nationId === result.dominantNationId ? "red" : "cyan"}
                  />
                  <span className="num">{Math.round(share * 100)}%</span>
                </div>
              ))}
            </div>

            <div className="result__portal-note muted">
              {t("ResultScreen.portal_score_note")}
            </div>

            <div className="result__foot">
              <span className="muted">
                {result.dominantNationId
                  ? t("ResultScreen.largest_power", {
                      p0: nameOf(result.dominantNationId),
                    })
                  : ""}
              </span>
              <Button tone="primary" onClick={() => window.location.reload()}>
                {t("ResultScreen.new_session")}
              </Button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
